import { useEffect, useMemo, useRef } from "react";

import type { BoardLayout } from "./boardLayout";
import type { BoardInput, BoardModel } from "./boardModel";
import { edgeStyle } from "./edgeStyle";
import {
  planMotion,
  replayPlan,
  type MotionPlan,
  type MotionSnapshot,
} from "./motion";
import { sampleMotion } from "./motionSample";
import { clockNow, useFrameClock } from "./useFrameClock";
import { useWaveTuning } from "./waveTuning";

/** What the motion planner compares between two renders of the board. */
export function motionSnapshot(
  input: BoardInput,
  model: BoardModel,
  layout: BoardLayout,
  streak: number,
  edgeScale: number,
): MotionSnapshot {
  const { palette } = input;
  const labelOf = new Map(layout.labels.map((l) => [l.id, l]));
  return {
    gameKey: `${input.startWord}|${input.targetWord}`,
    path: input.playerPath,
    status: input.status,
    streak,
    levels: input.edgeLevels ?? [],
    camera: layout.camera,
    pos: Object.fromEntries(
      layout.nodes.map((n) => [n.id, { px: n.px, py: n.py }]),
    ),
    playerEdges: model.links
      .filter((l) => l.type === "player")
      .map((l) => {
        const st = edgeStyle(l, { palette, scale: edgeScale, won: false });
        return { key: l.key, color: st.color, width: st.width };
      }),
    gold: palette.gold,
    pathColor: palette.path,
    options: layout.options.map((o) => {
      const l = o.existing ? undefined : labelOf.get(o.id);
      return {
        ...o,
        label: l ? { x: l.x, y: l.y, fontSize: l.fontSize } : undefined,
      };
    }),
  };
}

/**
 * Crunchy moves, the streak (weight, wave, shimmer, cool-down), the
 * camera glide and the win wave. Plans each change once, then samples the
 * plan every frame while it runs. `onWinEnd` fires when the win wave is over
 * (at once under reduced motion) and again on later renders of a won board;
 * it must be idempotent.
 */
export function useBoardMotion(
  snapshot: MotionSnapshot | null,
  enabled: boolean,
  reduced: boolean,
  onWinEnd: () => void,
) {
  const prev = useRef<MotionSnapshot | null>(null);
  const plan = useRef<MotionPlan | null>(null);

  if (snapshot && snapshot !== prev.current) {
    plan.current = enabled
      ? planMotion(prev.current, snapshot, clockNow(), reduced, plan.current)
      : null;
    prev.current = snapshot;
  }

  // The dev tuning panel's replay buttons.
  const replay = useWaveTuning((st) => st.replay);
  const replayed = useRef(replay?.nonce);
  if (replay && replay.nonce !== replayed.current) {
    replayed.current = replay.nonce;
    if (enabled && snapshot && !reduced) {
      plan.current = replayPlan(snapshot, replay.kind, clockNow());
    }
  }

  const current = plan.current;
  const now = useFrameClock(current ? current.end : 0, reduced);
  const sampleAt = current ? Math.max(now, current.t0) : now;
  const frame = useMemo(
    () => sampleMotion(current, sampleAt),
    [current, sampleAt],
  );

  const won = snapshot?.status === "won";
  const winPending =
    current?.winEnd !== undefined && Math.max(now, clockNow()) < current.winEnd;
  useEffect(() => {
    if (enabled && won && !winPending) onWinEnd();
  });

  return frame;
}
